import { Injectable, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { ModalDialogService } from './modal-dialog.service';
import { BehaviorSubject } from 'rxjs';
import { UserInterfaceService } from './user-interface.service';
import { ScreenType } from './screen-type.enum';

@Injectable({
  providedIn: 'root'
})
export class HtmlEditorService {

/*
    TinyMCE Events:

    onActivate, onAddUndo, onBeforeAddUndo, onBeforeExecCommand, onBeforeGetContent, onBeforeRenderUI, onBeforeSetContent, onBeforePaste, onBlur,
    onChange, onClearUndos, onClick, onContextMenu, onCopy, onCut, onDblclick, onDeactivate, onDirty, onDrag, onDragDrop, onDragEnd, onDragGesture,
    onDragOver, onDrop, onExecCommand, onFocus, onFocusIn, onFocusOut, onGetContent, onHide, onInit, onKeyDown, onKeyPress, onKeyUp, onLoadContent,
    onMouseDown, onMouseEnter, onMouseLeave, onMouseMove, onMouseOut, onMouseOver, onMouseUp, onNodeChange, onObjectResizeStart, onObjectResized,
    onObjectSelected, onPaste, onPostProcess, onPostRender, onPreProcess, onProgressState, onRedo, onRemove, onReset, onSaveContent, onSelectionChange,
    onSetAttrib, onSetContent, onShow, onSubmit, onUndo, onVisualAid
*/

  get editorConfig(): any {

    const config: any = {

      // these first two are required because we are loading TinyMCE with the main application...
      // per https://www.tiny.cloud/docs/integrations/angular/#tinymceangularintegrationquickstartguide

      // Root for resources
      base_url: '/tinymce',
      // Suffix to use when loading resources
      suffix: '.min',

      // Now we do the regular config stuff

      // "inline: false" fails to display the wysiwyg content... Needs a fixed height...
      inline: false,
      // height: '100%', // this does NOT show ANYTHING in the editor! (height is effectively zero...)
      // height: 300, // this does NOT fill the parent element properly!
      height: '25em',  // we set it to this, and then programmatically set the height on "div.tox.tox-tinymce"

      // "inline: true" kind of works... at least, well enough to get the functionality right, but it doesn't really look right...
      // inline: true,
      // toolbar_persist: true,

      branding: false,
      statusbar: false,
      menubar: false,

      plugins: [
        ' advlist anchor autolink help',
        ' lists link media noneditable preview code fullpage',  // fullpage is important to get ALL the content, not just the body...
        ' searchreplace table template visualblocks'
      ],
      toolbar:
        'a11ycheck undo redo | fontsizeselect | bold italic | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | link | removeformat',

      custom_elements: 'insert,resourceblock',
      extended_valid_elements: 'insert,resourceblock,+p[class]',
      // forced_root_block: false,

      // now we try to address WHICH formats to clear out on a "remove"
      // per https://www.tiny.cloud/docs/configure/content-formatting/
      formats: {
        removeformat: [
          {selector: 'b,strong,em,i,font,u,strike', remove : 'all', split : true, expand : false, block_expand: true, deep : true},
          // {selector: 'span', attributes : ['style', 'class'], remove : 'empty', split : true, expand : false, deep : true},
          {selector: '*', attributes : ['style', 'class'], remove : 'none', merge_siblings: false, split : false, expand : false, deep : true}
        ]
      }
    };

    return config;
  }

  public wysiwygMode: BehaviorSubject<boolean>;

  constructor(
    public modalDialogService: ModalDialogService,
    public userInterfaceService: UserInterfaceService,
  ) {
    this.wysiwygMode = new BehaviorSubject<boolean>(false);
  }

  get screenType(): ScreenType {
    return this.userInterfaceService.screenType.getValue();
  }

  // This debug code was used to figure out how to get TinyMCE to dynamically set its height...
  // public setEditorHeight(renderer: Renderer2, targetElement: HTMLElement, height: number): void {
  //   renderer.setStyle(targetElement, 'height', height + 'px');
  // }

  adjustEditorToFillAvailableHeight(parentElement: HTMLElement, renderer: Renderer2, internalEditorElementSelector?: string): void {
    internalEditorElementSelector = typeof internalEditorElementSelector !== 'undefined' ? internalEditorElementSelector  : 'editor > div.tox.tox-tinymce[role="application"]';

    // console.log('HtmlEditorService.adjustEditorToFillAvailableHeight() - internalEditorElementSelector: ' + internalEditorElementSelector);

    if (!parentElement) {
      return;
    }

    if (!renderer) {
      return;
    }

    const targetElement: HTMLElement = parentElement.querySelector(internalEditorElementSelector);

    // console.log('HtmlEditorService.adjustEditorToFillAvailableHeight()', 'parentElement:', parentElement, 'targetElement:', targetElement);

    if (targetElement) {

      renderer.removeStyle(targetElement, 'height');
      renderer.addClass(targetElement, 'fillAvailableHeight');

    } else {
      // console.log('HtmlEditorService.adjustEditorToFillAvailableHeight() - Unable to find internal editor element using selector: ' + internalEditorElementSelector);
    }
  }

  setEditorHeightAndWidthToFillWrapper(wrapperElement: HTMLElement, renderer: Renderer2, fudgeFactor?: number, internalEditorElementSelector?: string): void {
    fudgeFactor = typeof fudgeFactor === 'number' ? fudgeFactor : 0;
    internalEditorElementSelector = typeof internalEditorElementSelector !== 'undefined' ? internalEditorElementSelector  : 'editor > div.tox.tox-tinymce[role="application"]';

    // console.log('HtmlEditorService.setEditorHeightAndWidthToFillWrapper() - fudgeFactor: ' + fudgeFactor + ', internalEditorElementSelector: ' + internalEditorElementSelector);

    if (!wrapperElement) {
      return;
    }

    if (!renderer) {
      return;
    }

    const targetElement: HTMLElement = wrapperElement.querySelector(internalEditorElementSelector);

    // console.log('HtmlEditorService.setEditorHeightAndWidthToFillWrapper()', 'parentElement:', parentElement, 'targetElement:', targetElement);

    if (targetElement) {

      const parentHeight = wrapperElement.clientHeight;
      const parentWidth = wrapperElement.clientWidth;

      const targetOffset = this.userInterfaceService.getOffset(targetElement).top;
      const parentOffset = this.userInterfaceService.getOffset(wrapperElement).top;

      const height = parentHeight - (targetOffset - parentOffset) + fudgeFactor;
      const width = parentWidth * 0.95;

      // console.log('HtmlEditorService.setEditorHeightAndWidthToFillWrapper()', parentHeight, targetOffset, parentOffset, (targetOffset - parentOffset), fudgeFactor, height, parentWidth, width);

      // HACK ALERT: HtmlComponent in raw HTML mode requires "!important" on the textarea height. (We do NOT want that on the TinyMCE editor...)
      const styleFlags: RendererStyleFlags2 = (internalEditorElementSelector.indexOf('textarea') > -1 ? RendererStyleFlags2.Important : null);

      // console.log('HtmlEditorService.setEditorHeightAndWidthToFillWrapper()', height, styleFlags);

      renderer.removeStyle(targetElement, 'height');
      renderer.setStyle(targetElement, 'height', height + 'px', styleFlags);

      renderer.removeStyle(targetElement, 'width');
      renderer.setStyle(targetElement, 'width', width + 'px', styleFlags);

    } else {
      // console.log('HtmlEditorService.setEditorHeightAndWidthToFillWrapper() - Unable to find internal editor element using selector: ' + internalEditorElementSelector);
    }
  }

  cleanXHTML(dirtyHtml: string): string {
    let xhtml = dirtyHtml;
    xhtml = this._escapeHtmlEntities(xhtml);
    xhtml = this._stripScripts(xhtml);
    xhtml = this._formatXHTML(xhtml);

    // console.log('onEditorChange()', xhtml);

    return xhtml;
  }

  private _formatXHTML(xhtml: string): string {
    if (!xhtml) {
      return (null);
    }
    let formatted = '';

    const indentation = '\t';

    xhtml = xhtml.replace(/\r\n/g, '\n');   // clean up CRLFs...
    xhtml = xhtml.replace(/></g, '>\n<');   // put each element/node on a separate line...

    let depth = 0;

    const lines: string[] = xhtml.split('\n');
    // console.log('lines', lines);
    for (let line of lines) {
      // console.log('line', line);
      line = line.replace(/^\s*/g, '');     // clear out any leading whitespace chars...

      let indent = 0;
      if (line.match(/.+<\/\w[^>]*>$/)) {
        indent = 0;
      } else if (line.match(/^<\/\w/)) {
        if (depth !== 0) {
          depth -= 1;
        }
      } else if (line.match(/^<\w([^>]*[^\/])?>.*$/)) {
        indent = 1;
      } else {
        indent = 0;
      }

      formatted += this._formatLine(line, depth, indentation);

      depth += indent;
    }

    return formatted;
  }

  private _formatLine(line: string, depth: number, indentation: string): string {

    let formattedLine = '';

    const tagsWeWantToIndentInside = ['p', 'div', 'span', 'table', 'thead', 'tbody', 'tr'];

    let padding = '';
    for (let i = 0; i < depth; i++) {
      padding += indentation;
    }
    const matches = line.match(/^(<(\w*)( [^>]*){0,1}>)/);

    // console.log('================', matches);

    if (matches) {

      const thisTag = matches[2];

      if (tagsWeWantToIndentInside.includes(thisTag)) {
        const startTag = matches[0];
        const endTag = '</' + thisTag + '>';

        if (line.startsWith(startTag) && line.endsWith(endTag)) {

          formattedLine += padding + startTag + '\n';
          formattedLine += padding + indentation + line.substr(startTag.length, line.length - (startTag.length + endTag.length)) + '\n';
          formattedLine += padding + endTag + '\n';
        }
      }

    } else {
      formattedLine += padding + line + '\n';
    }

    if (formattedLine === '') {
      formattedLine += padding + line + '\n';
    }

    return (formattedLine);
  }

  private _stripScripts(s: string): string {

    // console.log('stripScripts() - BEFORE:\n' + s);

    let s2 = s;

    try {
      const doc = document.implementation.createHTMLDocument();
      doc.open();
      doc.write(s);
      doc.close();

      const scripts = doc.documentElement.getElementsByTagName('script');
      let i = scripts.length;

      if (i > 0) {

        this.modalDialogService.showAlert('To ensure that we do not propagate any nefarious code, we have stripped out all the scripts from the email template.', 'Please Note...');

        while (i--) {
          scripts[i].parentNode.removeChild(scripts[i]);
        }

        const preHead = s.substr(0, s.toLowerCase().indexOf('<head'));
        const postBody = s.substr(s.toLowerCase().lastIndexOf('</body>') + 7);

        s2 = preHead + doc.documentElement.innerHTML + postBody;
      }

    } catch (ex) {
      const msg = 'stripScripts()\n';
      this.modalDialogService.showAlert(msg + '\n' + JSON.stringify(ex), 'Error stripping scripts from HTML');
    }

    // console.log('stripScripts() - AFTER:\n' + s2);

    return s2;
  }

  private _escapeHtmlEntities(text: string): string {

    const entityTable = {
      // we don't do these...
      // 34: 'quot',  38: 'amp',  39: 'apos', 60: 'lt',   62: 'gt',

      // we do these...
      160: 'nbsp',	161: 'iexcl',	162: 'cent',	163: 'pound',	164: 'curren',	165: 'yen',	166: 'brvbar',	167: 'sect',
      168: 'uml',	169: 'copy',	170: 'ordf',	171: 'laquo',	172: 'not',	173: 'shy',	174: 'reg',	175: 'macr',	176: 'deg',
      177: 'plusmn',	178: 'sup2',	179: 'sup3',	180: 'acute',	181: 'micro',	182: 'para',	183: 'middot',	184: 'cedil',
      185: 'sup1',	186: 'ordm',	187: 'raquo',	188: 'frac14',	189: 'frac12',	190: 'frac34',	191: 'iquest',	192: 'Agrave',
      193: 'Aacute',	194: 'Acirc',	195: 'Atilde',	196: 'Auml',	197: 'Aring',	198: 'AElig',	199: 'Ccedil',	200: 'Egrave',
      201: 'Eacute',	202: 'Ecirc',	203: 'Euml',	204: 'Igrave',	205: 'Iacute',	206: 'Icirc',	207: 'Iuml',	208: 'ETH',
      209: 'Ntilde',	210: 'Ograve',	211: 'Oacute',	212: 'Ocirc',	213: 'Otilde',	214: 'Ouml',	215: 'times',	216: 'Oslash',
      217: 'Ugrave',	218: 'Uacute',	219: 'Ucirc',	220: 'Uuml',	221: 'Yacute',	222: 'THORN',	223: 'szlig',	224: 'agrave',
      225: 'aacute',	226: 'acirc',	227: 'atilde',	228: 'auml',	229: 'aring',	230: 'aelig',	231: 'ccedil',	232: 'egrave',
      233: 'eacute',	234: 'ecirc',	235: 'euml',	236: 'igrave',	237: 'iacute',	238: 'icirc',	239: 'iuml',	240: 'eth',
      241: 'ntilde',	242: 'ograve',	243: 'oacute',	244: 'ocirc',	245: 'otilde',	246: 'ouml',	247: 'divide',	248: 'oslash',
      249: 'ugrave',	250: 'uacute',	251: 'ucirc',	252: 'uuml',	253: 'yacute',	254: 'thorn',	255: 'yuml',	402: 'fnof',
      913: 'Alpha',	914: 'Beta',	915: 'Gamma',	916: 'Delta',	917: 'Epsilon',	918: 'Zeta',	919: 'Eta',	920: 'Theta',
      921: 'Iota',	922: 'Kappa',	923: 'Lambda',	924: 'Mu',	925: 'Nu',	926: 'Xi',	927: 'Omicron',	928: 'Pi',	929: 'Rho',
      931: 'Sigma',	932: 'Tau',	933: 'Upsilon',	934: 'Phi',	935: 'Chi',	936: 'Psi',	937: 'Omega',	945: 'alpha',	946: 'beta',
      947: 'gamma',	948: 'delta',	949: 'epsilon',	950: 'zeta',	951: 'eta',	952: 'theta',	953: 'iota',	954: 'kappa',
      955: 'lambda',	956: 'mu',	957: 'nu',	958: 'xi',	959: 'omicron',	960: 'pi',	961: 'rho',	962: 'sigmaf',	963: 'sigma',
      964: 'tau',	965: 'upsilon',	966: 'phi',	967: 'chi',	968: 'psi',	969: 'omega',	977: 'thetasym',	978: 'upsih',	982: 'piv',
      8226: 'bull',	8230: 'hellip',	8242: 'prime',	8243: 'Prime',	8254: 'oline',	8260: 'frasl',	8472: 'weierp',	8465: 'image',
      8476: 'real',	8482: 'trade',	8501: 'alefsym',	8592: 'larr',	8593: 'uarr',	8594: 'rarr',	8595: 'darr',	8596: 'harr',
      8629: 'crarr',	8656: 'lArr',	8657: 'uArr',	8658: 'rArr',	8659: 'dArr',	8660: 'hArr',	8704: 'forall',	8706: 'part',
      8707: 'exist',	8709: 'empty',	8711: 'nabla',	8712: 'isin',	8713: 'notin',	8715: 'ni',	8719: 'prod',	8721: 'sum',
      8722: 'minus',	8727: 'lowast',	8730: 'radic',	8733: 'prop',	8734: 'infin',	8736: 'ang',	8743: 'and',	8744: 'or',
      8745: 'cap',	8746: 'cup',	8747: 'int',	8756: 'there4',	8764: 'sim',	8773: 'cong',	8776: 'asymp',	8800: 'ne',
      8801: 'equiv',	8804: 'le',	8805: 'ge',	8834: 'sub',	8835: 'sup',	8836: 'nsub',	8838: 'sube',	8839: 'supe',
      8853: 'oplus',	8855: 'otimes',	8869: 'perp',	8901: 'sdot',	8968: 'lceil',	8969: 'rceil',	8970: 'lfloor',	8971: 'rfloor',
      9001: 'lang',	9002: 'rang',	9674: 'loz',	9824: 'spades',	9827: 'clubs',	9829: 'hearts',	9830: 'diams',	338: 'OElig',
      339: 'oelig',	352: 'Scaron',	353: 'scaron',	376: 'Yuml',	710: 'circ',	732: 'tilde',	8194: 'ensp',	8195: 'emsp',
      8201: 'thinsp',	8204: 'zwnj',	8205: 'zwj',	8206: 'lrm',	8207: 'rlm',	8211: 'ndash',	8212: 'mdash',	8216: 'lsquo',
      8217: 'rsquo',	8218: 'sbquo',	8220: 'ldquo',	8221: 'rdquo',	8222: 'bdquo',	8224: 'dagger',	8225: 'Dagger',	8240: 'permil',
      8249: 'lsaquo',	8250: 'rsaquo',	8364: 'euro'
    };

    return text.replace(/[\u00A0-\u2666<>\&]/g, (c) => {
      const charCode = c.charCodeAt(0);
      const entityName = entityTable[charCode];
      // console.log('c: ' + c + ' --> charCode: ' + charCode + '--> entityName: ' + entityName);
      return (entityName ? ('&' + entityName + ';') : c);
    });
  }

}

//////////////////////////////////////////////////////////////////////////////////////////////
// Source code for the deprecated "fullpage" plugin, which will be removed in TinyMCE v6.0
// per https://github.com/tinymce/tinymce-dist/blob/5.8.2/plugins/fullpage/plugin.js
//////////////////////////////////////////////////////////////////////////////////////////////

// /**
//  * Copyright (c) Tiny Technologies, Inc. All rights reserved.
//  * Licensed under the LGPL or a commercial license.
//  * For LGPL see License.txt in the project root for license information.
//  * For commercial licenses see https://www.tiny.cloud/
//  *
//  * Version: 5.8.2 (2021-06-23)
//  */
// (function () {
//     'use strict';

//     var Cell = function (initial) {
//       var value = initial;
//       var get = function () {
//         return value;
//       };
//       var set = function (v) {
//         value = v;
//       };
//       return {
//         get: get,
//         set: set
//       };
//     };

//     var global = tinymce.util.Tools.resolve('tinymce.PluginManager');

//     var __assign = function () {
//       __assign = Object.assign || function __assign(t) {
//         for (var s, i = 1, n = arguments.length; i < n; i++) {
//           s = arguments[i];
//           for (var p in s)
//             if (Object.prototype.hasOwnProperty.call(s, p))
//               t[p] = s[p];
//         }
//         return t;
//       };
//       return __assign.apply(this, arguments);
//     };

//     var global$1 = tinymce.util.Tools.resolve('tinymce.util.Tools');

//     var global$2 = tinymce.util.Tools.resolve('tinymce.html.DomParser');

//     var global$3 = tinymce.util.Tools.resolve('tinymce.html.Node');

//     var global$4 = tinymce.util.Tools.resolve('tinymce.html.Serializer');

//     var shouldHideInSourceView = function (editor) {
//       return editor.getParam('fullpage_hide_in_source_view');
//     };
//     var getDefaultXmlPi = function (editor) {
//       return editor.getParam('fullpage_default_xml_pi');
//     };
//     var getDefaultEncoding = function (editor) {
//       return editor.getParam('fullpage_default_encoding');
//     };
//     var getDefaultFontFamily = function (editor) {
//       return editor.getParam('fullpage_default_font_family');
//     };
//     var getDefaultFontSize = function (editor) {
//       return editor.getParam('fullpage_default_font_size');
//     };
//     var getDefaultTextColor = function (editor) {
//       return editor.getParam('fullpage_default_text_color');
//     };
//     var getDefaultTitle = function (editor) {
//       return editor.getParam('fullpage_default_title');
//     };
//     var getDefaultDocType = function (editor) {
//       return editor.getParam('fullpage_default_doctype', '<!DOCTYPE html>');
//     };
//     var getProtect = function (editor) {
//       return editor.getParam('protect');
//     };

//     var parseHeader = function (head) {
//       return global$2({
//         validate: false,
//         root_name: '#document'
//       }).parse(head, { format: 'xhtml' });
//     };
//     var htmlToData = function (editor, head) {
//       var headerFragment = parseHeader(head);
//       var data = {};
//       var elm, matches;
//       var getAttr = function (elm, name) {
//         var value = elm.attr(name);
//         return value || '';
//       };
//       data.fontface = getDefaultFontFamily(editor);
//       data.fontsize = getDefaultFontSize(editor);
//       elm = headerFragment.firstChild;
//       if (elm.type === 7) {
//         data.xml_pi = true;
//         matches = /encoding="([^"]+)"/.exec(elm.value);
//         if (matches) {
//           data.docencoding = matches[1];
//         }
//       }
//       elm = headerFragment.getAll('#doctype')[0];
//       if (elm) {
//         data.doctype = '<!DOCTYPE' + elm.value + '>';
//       }
//       elm = headerFragment.getAll('title')[0];
//       if (elm && elm.firstChild) {
//         data.title = elm.firstChild.value;
//       }
//       global$1.each(headerFragment.getAll('meta'), function (meta) {
//         var name = meta.attr('name');
//         var httpEquiv = meta.attr('http-equiv');
//         var matches;
//         if (name) {
//           data[name.toLowerCase()] = meta.attr('content');
//         } else if (httpEquiv === 'Content-Type') {
//           matches = /charset\s*=\s*(.*)\s*/gi.exec(meta.attr('content'));
//           if (matches) {
//             data.docencoding = matches[1];
//           }
//         }
//       });
//       elm = headerFragment.getAll('html')[0];
//       if (elm) {
//         data.langcode = getAttr(elm, 'lang') || getAttr(elm, 'xml:lang');
//       }
//       data.stylesheets = [];
//       global$1.each(headerFragment.getAll('link'), function (link) {
//         if (link.attr('rel') === 'stylesheet') {
//           data.stylesheets.push(link.attr('href'));
//         }
//       });
//       elm = headerFragment.getAll('body')[0];
//       if (elm) {
//         data.langdir = getAttr(elm, 'dir');
//         data.style = getAttr(elm, 'style');
//         data.visited_color = getAttr(elm, 'vlink');
//         data.link_color = getAttr(elm, 'link');
//         data.active_color = getAttr(elm, 'alink');
//       }
//       return data;
//     };
//     var dataToHtml = function (editor, data, head) {
//       var headElement, elm, value;
//       var dom = editor.dom;
//       var setAttr = function (elm, name, value) {
//         elm.attr(name, value ? value : undefined);
//       };
//       var addHeadNode = function (node) {
//         if (headElement.firstChild) {
//           headElement.insert(node, headElement.firstChild);
//         } else {
//           headElement.append(node);
//         }
//       };
//       var headerFragment = parseHeader(head);
//       headElement = headerFragment.getAll('head')[0];
//       if (!headElement) {
//         elm = headerFragment.getAll('html')[0];
//         headElement = new global$3('head', 1);
//         if (elm.firstChild) {
//           elm.insert(headElement, elm.firstChild, true);
//         } else {
//           elm.append(headElement);
//         }
//       }
//       elm = headerFragment.firstChild;
//       if (data.xml_pi) {
//         value = 'version="1.0"';
//         if (data.docencoding) {
//           value += ' encoding="' + data.docencoding + '"';
//         }
//         if (elm.type !== 7) {
//           elm = new global$3('xml', 7);
//           headerFragment.insert(elm, headerFragment.firstChild, true);
//         }
//         elm.value = value;
//       } else if (elm && elm.type === 7) {
//         elm.remove();
//       }
//       elm = headerFragment.getAll('#doctype')[0];
//       if (data.doctype) {
//         if (!elm) {
//           elm = new global$3('#doctype', 10);
//           if (data.xml_pi) {
//             headerFragment.insert(elm, headerFragment.firstChild);
//           } else {
//             addHeadNode(elm);
//           }
//         }
//         elm.value = data.doctype.substring(9, data.doctype.length - 1);
//       } else if (elm) {
//         elm.remove();
//       }
//       elm = null;
//       global$1.each(headerFragment.getAll('meta'), function (meta) {
//         if (meta.attr('http-equiv') === 'Content-Type') {
//           elm = meta;
//         }
//       });
//       if (data.docencoding) {
//         if (!elm) {
//           elm = new global$3('meta', 1);
//           elm.attr('http-equiv', 'Content-Type');
//           elm.shortEnded = true;
//           addHeadNode(elm);
//         }
//         elm.attr('content', 'text/html; charset=' + data.docencoding);
//       } else if (elm) {
//         elm.remove();
//       }
//       elm = headerFragment.getAll('title')[0];
//       if (data.title) {
//         if (!elm) {
//           elm = new global$3('title', 1);
//           addHeadNode(elm);
//         } else {
//           elm.empty();
//         }
//         elm.append(new global$3('#text', 3)).value = data.title;
//       } else if (elm) {
//         elm.remove();
//       }
//       global$1.each('keywords,description,author,copyright,robots'.split(','), function (name) {
//         var nodes = headerFragment.getAll('meta');
//         var i, meta;
//         var value = data[name];
//         for (i = 0; i < nodes.length; i++) {
//           meta = nodes[i];
//           if (meta.attr('name') === name) {
//             if (value) {
//               meta.attr('content', value);
//             } else {
//               meta.remove();
//             }
//             return;
//           }
//         }
//         if (value) {
//           elm = new global$3('meta', 1);
//           elm.attr('name', name);
//           elm.attr('content', value);
//           elm.shortEnded = true;
//           addHeadNode(elm);
//         }
//       });
//       var currentStyleSheetsMap = {};
//       global$1.each(headerFragment.getAll('link'), function (stylesheet) {
//         if (stylesheet.attr('rel') === 'stylesheet') {
//           currentStyleSheetsMap[stylesheet.attr('href')] = stylesheet;
//         }
//       });
//       global$1.each(data.stylesheets, function (stylesheet) {
//         if (!currentStyleSheetsMap[stylesheet]) {
//           elm = new global$3('link', 1);
//           elm.attr({
//             rel: 'stylesheet',
//             text: 'text/css',
//             href: stylesheet
//           });
//           elm.shortEnded = true;
//           addHeadNode(elm);
//         }
//         delete currentStyleSheetsMap[stylesheet];
//       });
//       global$1.each(currentStyleSheetsMap, function (stylesheet) {
//         stylesheet.remove();
//       });
//       elm = headerFragment.getAll('body')[0];
//       if (elm) {
//         setAttr(elm, 'dir', data.langdir);
//         setAttr(elm, 'style', data.style);
//         setAttr(elm, 'vlink', data.visited_color);
//         setAttr(elm, 'link', data.link_color);
//         setAttr(elm, 'alink', data.active_color);
//         dom.setAttribs(editor.getBody(), {
//           style: data.style,
//           dir: data.dir,
//           vLink: data.visited_color,
//           link: data.link_color,
//           aLink: data.active_color
//         });
//       }
//       elm = headerFragment.getAll('html')[0];
//       if (elm) {
//         setAttr(elm, 'lang', data.langcode);
//         setAttr(elm, 'xml:lang', data.langcode);
//       }
//       if (!headElement.firstChild) {
//         headElement.remove();
//       }
//       var html = global$4({
//         validate: false,
//         indent: true,
//         indent_before: 'head,html,body,meta,title,script,link,style',
//         indent_after: 'head,html,body,meta,title,script,link,style'
//       }).serialize(headerFragment);
//       return html.substring(0, html.indexOf('</body>'));
//     };

//     var open = function (editor, headState) {
//       var data = htmlToData(editor, headState.get());
//       var defaultData = {
//         title: '',
//         keywords: '',
//         description: '',
//         robots: '',
//         author: '',
//         docencoding: ''
//       };
//       var initialData = __assign(__assign({}, defaultData), data);
//       editor.windowManager.open({
//         title: 'Metadata and Document Properties',
//         size: 'normal',
//         body: {
//           type: 'panel',
//           items: [
//             {
//               name: 'title',
//               type: 'input',
//               label: 'Title'
//             },
//             {
//               name: 'keywords',
//               type: 'input',
//               label: 'Keywords'
//             },
//             {
//               name: 'description',
//               type: 'input',
//               label: 'Description'
//             },
//             {
//               name: 'robots',
//               type: 'input',
//               label: 'Robots'
//             },
//             {
//               name: 'author',
//               type: 'input',
//               label: 'Author'
//             },
//             {
//               name: 'docencoding',
//               type: 'input',
//               label: 'Encoding'
//             }
//           ]
//         },
//         buttons: [
//           {
//             type: 'cancel',
//             name: 'cancel',
//             text: 'Cancel'
//           },
//           {
//             type: 'submit',
//             name: 'save',
//             text: 'Save',
//             primary: true
//           }
//         ],
//         initialData: initialData,
//         onSubmit: function (api) {
//           var nuData = api.getData();
//           var headHtml = dataToHtml(editor, global$1.extend(data, nuData), headState.get());
//           headState.set(headHtml);
//           api.close();
//         }
//       });
//     };

//     var register = function (editor, headState) {
//       editor.addCommand('mceFullPageProperties', function () {
//         open(editor, headState);
//       });
//     };

//     var protectHtml = function (protect, html) {
//       global$1.each(protect, function (pattern) {
//         html = html.replace(pattern, function (str) {
//           return '<!--mce:protected ' + escape(str) + '-->';
//         });
//       });
//       return html;
//     };
//     var unprotectHtml = function (html) {
//       return html.replace(/<!--mce:protected ([\s\S]*?)-->/g, function (a, m) {
//         return unescape(m);
//       });
//     };

//     var each = global$1.each;
//     var low = function (s) {
//       return s.replace(/<\/?[A-Z]+/g, function (a) {
//         return a.toLowerCase();
//       });
//     };
//     var handleSetContent = function (editor, headState, footState, evt) {
//       var startPos, endPos, content, styles = '';
//       var dom = editor.dom;
//       if (evt.selection) {
//         return;
//       }
//       content = protectHtml(getProtect(editor), evt.content);
//       if (evt.format === 'raw' && headState.get()) {
//         return;
//       }
//       if (evt.source_view && shouldHideInSourceView(editor)) {
//         return;
//       }
//       if (content.length === 0 && !evt.source_view) {
//         content = global$1.trim(headState.get()) + '\n' + global$1.trim(content) + '\n' + global$1.trim(footState.get());
//       }
//       content = content.replace(/<(\/?)BODY/gi, '<$1body');
//       startPos = content.indexOf('<body');
//       if (startPos !== -1) {
//         startPos = content.indexOf('>', startPos);
//         headState.set(low(content.substring(0, startPos + 1)));
//         endPos = content.indexOf('</body', startPos);
//         if (endPos === -1) {
//           endPos = content.length;
//         }
//         evt.content = global$1.trim(content.substring(startPos + 1, endPos));
//         footState.set(low(content.substring(endPos)));
//       } else {
//         headState.set(getDefaultHeader(editor));
//         footState.set('\n</body>\n</html>');
//       }
//       var headerFragment = parseHeader(headState.get());
//       each(headerFragment.getAll('style'), function (node) {
//         if (node.firstChild) {
//           styles += node.firstChild.value;
//         }
//       });
//       var bodyElm = headerFragment.getAll('body')[0];
//       if (bodyElm) {
//         dom.setAttribs(editor.getBody(), {
//           style: bodyElm.attr('style') || '',
//           dir: bodyElm.attr('dir') || '',
//           vLink: bodyElm.attr('vlink') || '',
//           link: bodyElm.attr('link') || '',
//           aLink: bodyElm.attr('alink') || ''
//         });
//       }
//       dom.remove('fullpage_styles');
//       var headElm = editor.getDoc().getElementsByTagName('head')[0];
//       if (styles) {
//         var styleElm = dom.add(headElm, 'style', { id: 'fullpage_styles' });
//         styleElm.appendChild(document.createTextNode(styles));
//       }
//       var currentStyleSheetsMap = {};
//       global$1.each(headElm.getElementsByTagName('link'), function (stylesheet) {
//         if (stylesheet.rel === 'stylesheet' && stylesheet.getAttribute('data-mce-fullpage')) {
//           currentStyleSheetsMap[stylesheet.href] = stylesheet;
//         }
//       });
//       global$1.each(headerFragment.getAll('link'), function (stylesheet) {
//         var href = stylesheet.attr('href');
//         if (!href) {
//           return true;
//         }
//         if (!currentStyleSheetsMap[href] && stylesheet.attr('rel') === 'stylesheet') {
//           dom.add(headElm, 'link', {
//             'rel': 'stylesheet',
//             'text': 'text/css',
//             href: href,
//             'data-mce-fullpage': '1'
//           });
//         }
//         delete currentStyleSheetsMap[href];
//       });
//       global$1.each(currentStyleSheetsMap, function (stylesheet) {
//         stylesheet.parentNode.removeChild(stylesheet);
//       });
//     };
//     var getDefaultHeader = function (editor) {
//       var header = '', value, styles = '';
//       if (getDefaultXmlPi(editor)) {
//         var piEncoding = getDefaultEncoding(editor);
//         header += '<?xml version="1.0" encoding="' + (piEncoding ? piEncoding : 'ISO-8859-1') + '" ?>\n';
//       }
//       header += getDefaultDocType(editor);
//       header += '\n<html>\n<head>\n';
//       if (value = getDefaultTitle(editor)) {
//         header += '<title>' + value + '</title>\n';
//       }
//       if (value = getDefaultEncoding(editor)) {
//         header += '<meta http-equiv="Content-Type" content="text/html; charset=' + value + '" />\n';
//       }
//       if (value = getDefaultFontFamily(editor)) {
//         styles += 'font-family: ' + value + ';';
//       }
//       if (value = getDefaultFontSize(editor)) {
//         styles += 'font-size: ' + value + ';';
//       }
//       if (value = getDefaultTextColor(editor)) {
//         styles += 'color: ' + value + ';';
//       }
//       header += '</head>\n<body' + (styles ? ' style="' + styles + '"' : '') + '>\n';
//       return header;
//     };
//     var handleGetContent = function (editor, head, foot, evt) {
//       if (evt.format === 'html' && !evt.selection && (!evt.source_view || !shouldHideInSourceView(editor))) {
//         evt.content = unprotectHtml(global$1.trim(head) + '\n' + global$1.trim(evt.content) + '\n' + global$1.trim(foot));
//       }
//     };
//     var setup = function (editor, headState, footState) {
//       editor.on('BeforeSetContent', function (evt) {
//         handleSetContent(editor, headState, footState, evt);
//       });
//       editor.on('GetContent', function (evt) {
//         handleGetContent(editor, headState.get(), footState.get(), evt);
//       });
//     };

//     var register$1 = function (editor) {
//       editor.ui.registry.addButton('fullpage', {
//         tooltip: 'Metadata and document properties',
//         icon: 'document-properties',
//         onAction: function () {
//           editor.execCommand('mceFullPageProperties');
//         }
//       });
//       editor.ui.registry.addMenuItem('fullpage', {
//         text: 'Metadata and document properties',
//         icon: 'document-properties',
//         onAction: function () {
//           editor.execCommand('mceFullPageProperties');
//         }
//       });
//     };

//     function Plugin () {
//       global.add('fullpage', function (editor) {
//         var headState = Cell(''), footState = Cell('');
//         register(editor, headState);
//         register$1(editor);
//         setup(editor, headState, footState);
//       });
//     }

//     Plugin();

// }());
